import type { ClientFilterConfig, FilterPage } from "src/services/types";

export type { FilterPage };

/** Config-driven multi-select filter values keyed by GraphQL requestField. */
export type DynamicMultiSelectValues = Record<string, string[]>;

/** Home analytics/query variables: known scalars plus dynamic multi-select fields. */
export type HomeAnalyticsConfig = {
  startDate?: string;
  endDate?: string;
  procedureType: string[];
  search?: string;
  systemFilter: string[];
  dateRangeFilterField: string;
  [requestField: string]: string[] | string | undefined;
};

/** Scalar queue page fields (non-array GraphQL variables). */
export type QueuePageScalars = {
  search: string;
  startDate?: string;
  endDate?: string;
  dateRangeFilterField: string;
  procedureType: string[];
  specialtyReviewFilter?: string;
  /**
   * Queue Escalated tab filter when a tableTabs entry has filterKey needs_human_rs_review.
   * true → needsHumanRsReview GraphQL arg; omit/undefined → All (no arg).
   * Not used on All Claims. Independent of other future Queue tabs.
   */
  needsHumanRsReview?: boolean;
};

/** Queue refetch state: scalars plus generic multi-select filter values by requestField. */
export type QueuePageFilterState = QueuePageScalars & {
  [requestField: string]: string[] | string | boolean | undefined;
};

/** Filter is shown only when `enabled` is explicitly true; omitted or false hides it. */
export function isClientFilterEnabled(filter: ClientFilterConfig): boolean {
  return filter.enabled === true;
}

function filterAppliesToPage(
  filter: ClientFilterConfig,
  page: FilterPage
): boolean {
  const pages = filter.pages;
  if (!pages?.length) {
    return page === "queue"; // Default to queue if no pages are specified
  }
  return pages.includes(page);
}

/** Subset of queueFilters whose `pages` array includes the given page. */
export function getFiltersForPage(
  queueFilters: Record<string, ClientFilterConfig> | undefined,
  page: FilterPage
): Record<string, ClientFilterConfig> {
  if (!queueFilters) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(queueFilters).filter(([, filter]) =>
      filterAppliesToPage(filter, page)
    )
  );
}

/** Filters scoped to the home (all claims) page. */
export function getHomePageFilters(
  queueFilters: Record<string, ClientFilterConfig> | undefined
): Record<string, ClientFilterConfig> {
  return getFiltersForPage(queueFilters, "home");
}

/** Filters scoped to the queue page. */
export function getQueuePageFilters(
  queueFilters: Record<string, ClientFilterConfig> | undefined
): Record<string, ClientFilterConfig> {
  return getFiltersForPage(queueFilters, "queue");
}

/** Stable sort by `order` then filter id. */
export function sortedFilterConfigEntries(
  filters: Record<string, ClientFilterConfig> | undefined
): Array<{ id: string; filter: ClientFilterConfig }> {
  if (!filters) {
    return [];
  }
  return Object.entries(filters)
    .map(([id, filter]) => ({ id, filter }))
    .sort((a, b) => {
      const ao = a.filter.order ?? 0;
      const bo = b.filter.order ?? 0;
      if (ao !== bo) {
        return ao - bo;
      }
      return a.id.localeCompare(b.id);
    });
}

function isEnabledMultiSelectFilter(filter: ClientFilterConfig): boolean {
  return (
    isClientFilterEnabled(filter) &&
    filter.type === "multiSelect" &&
    Boolean(filter.requestField?.trim())
  );
}

function isRenderableMultiSelectFilter(filter: ClientFilterConfig): boolean {
  return isEnabledMultiSelectFilter(filter) && Boolean(filter.values?.length);
}

/** True when at least one config entry would render in `DynamicFilters`. */
export function hasRenderableDynamicFilters(
  filters: Record<string, ClientFilterConfig> | undefined
): boolean {
  return Object.values(filters ?? {}).some(isRenderableMultiSelectFilter);
}

/** Returns unique requestField names for enabled multiSelect filters. */
export function getEnabledMultiSelectRequestFields(
  filters: Record<string, ClientFilterConfig> | undefined
): string[] {
  if (!filters) {
    return [];
  }
  const fields = new Set<string>();
  for (const filter of Object.values(filters)) {
    if (isEnabledMultiSelectFilter(filter) && filter.requestField) {
      fields.add(filter.requestField.trim());
    }
  }
  return Array.from(fields).sort();
}

/** Builds `{ [requestField]: [] }` for all enabled multiSelect filters. */
export function buildDynamicFilterDefaults(
  filters: Record<string, ClientFilterConfig> | undefined
): DynamicMultiSelectValues {
  const defaults: DynamicMultiSelectValues = {};
  for (const field of getEnabledMultiSelectRequestFields(filters)) {
    defaults[field] = [];
  }
  return defaults;
}

function asStringArray(raw: unknown): string[] {
  return Array.isArray(raw)
    ? raw.filter((item): item is string => typeof item === "string")
    : [];
}

/** Queue scalar fields that must not be overwritten by config-driven multi-select filters. */
export const RESERVED_QUEUE_SCALAR_FIELDS = new Set([
  "search",
  "startDate",
  "endDate",
  "dateRangeFilterField",
  "procedureType",
  "specialtyReviewFilter",
  "needsHumanRsReview",
]);

/** Canonical persisted/GraphQL variable format: array of procedure keys. */
export function normalizeProcedureType(raw: unknown): string[] {
  if (Array.isArray(raw)) {
    const keys = raw.filter(
      (item): item is string =>
        typeof item === "string" && item.trim().length > 0
    );
    return keys.length ? keys : ["ALL"];
  }

  if (typeof raw === "string" && raw.trim()) {
    const keys = raw
      .split(",")
      .map((segment) => segment.trim())
      .filter(Boolean);
    return keys.length ? keys : ["ALL"];
  }

  return ["ALL"];
}

/** True when normalized procedure selection differs from stored config (for pagination reset). */
export function procedureTypeSelectionChanged(
  stored: unknown,
  selected: unknown
): boolean {
  const normalizedStored = [...normalizeProcedureType(stored)].sort();
  const normalizedSelected = [...normalizeProcedureType(selected)].sort();
  return (
    JSON.stringify(normalizedStored) !== JSON.stringify(normalizedSelected)
  );
}

/** True when saved config still stores procedureType as a legacy comma/single string. */
export function needsProcedureTypeFormatNormalization(
  source: Record<string, unknown> | undefined
): boolean {
  const procedureType = source?.procedureType;
  return procedureType != null && !Array.isArray(procedureType);
}

/** Legacy queue multi-select fields required by GraphQL when not config-driven. */
export const LEGACY_QUEUE_FILTER_FIELDS = [
  "ingressFilter",
  "stateFilter",
] as const;

/** Default empty arrays for legacy queue filter GraphQL variables. */
export function buildLegacyQueueFilterDefaults(): DynamicMultiSelectValues {
  return {
    ingressFilter: [],
    stateFilter: [],
  };
}

/** Reads legacy queue filter values; missing keys default to `[]`. */
export function pickLegacyQueueFilterValues(
  source: Record<string, unknown> | undefined
): DynamicMultiSelectValues {
  const src = source || {};
  return {
    ingressFilter: asStringArray(src.ingressFilter),
    stateFilter: asStringArray(src.stateFilter),
  };
}

/** Reads config-driven multi-select values; skips reserved queue scalar requestFields. */
export function pickQueueDynamicFilterValues(
  source: Record<string, unknown> | undefined,
  filters: Record<string, ClientFilterConfig> | undefined
): DynamicMultiSelectValues {
  return pickDynamicFilterValues(source, filters, RESERVED_QUEUE_SCALAR_FIELDS);
}

/** Reads legacy + config-driven multi-select filter values from saved config. */
export function pickQueueMultiSelectFilterValues(
  source: Record<string, unknown> | undefined,
  queueFilters: Record<string, ClientFilterConfig> | undefined
): DynamicMultiSelectValues {
  return {
    ...pickLegacyQueueFilterValues(source),
    ...pickQueueDynamicFilterValues(source, queueFilters),
  };
}

/** Reads a multi-select filter array from queue page state by requestField. */
export function getQueueFilterValues(
  state: QueuePageFilterState,
  requestField: string
): string[] {
  return asStringArray(state[requestField]);
}

/** True when saved config is missing a non-null array for a queue filter field. */
export function needsQueueTableFilterNormalization(
  source: Record<string, unknown> | undefined,
  filters: Record<string, ClientFilterConfig> | undefined
): boolean {
  const src = source || {};
  const dynamicFields = getEnabledMultiSelectRequestFields(filters).filter(
    (field) => !RESERVED_QUEUE_SCALAR_FIELDS.has(field)
  );
  const fields = [...LEGACY_QUEUE_FILTER_FIELDS, ...dynamicFields];
  return fields.some((field) => !Array.isArray(src[field]));
}

/** Normalizes queue GraphQL variables with non-null filter arrays. */
export function buildQueueQueryVariables(
  queueTableConfig: Record<string, unknown>,
  effectiveDateRangeFilterField: string,
  queueFilters: Record<string, ClientFilterConfig> | undefined
): Record<string, unknown> {
  const vars: Record<string, unknown> = {
    ...queueTableConfig,
    ...buildQueuePageFilterState(
      queueTableConfig,
      effectiveDateRangeFilterField,
      queueFilters
    ),
  };
  // All tab persists needsHumanRsReview: false for UI; omit from GraphQL unless Escalated.
  if (vars.needsHumanRsReview !== true) {
    delete vars.needsHumanRsReview;
  }
  return vars;
}

/** Reads configured requestField values from a config object; missing keys default to `[]`. */
export function pickDynamicFilterValues(
  source: Record<string, unknown> | undefined,
  filters: Record<string, ClientFilterConfig> | undefined,
  skipFields?: ReadonlySet<string>
): DynamicMultiSelectValues {
  const values: DynamicMultiSelectValues = {};
  const src = source || {};
  for (const field of getEnabledMultiSelectRequestFields(filters)) {
    if (skipFields?.has(field)) {
      continue;
    }
    const raw = src[field];
    values[field] = Array.isArray(raw) ? (raw as string[]) : [];
  }
  return values;
}

/** Merges saved dynamic filter values onto base config; missing keys default to `[]`. */
export function mergeDynamicFilterState<T extends Record<string, unknown>>(
  baseConfig: T,
  savedConfig: Record<string, unknown> | undefined,
  filters: Record<string, ClientFilterConfig> | undefined
): T & DynamicMultiSelectValues {
  return {
    ...baseConfig,
    ...pickDynamicFilterValues(savedConfig, filters),
  };
}

/** Copies only configured dynamic filter fields from source (for persistence helpers). */
export function pickDynamicFilterPatch(
  source: Record<string, unknown> | undefined,
  filters: Record<string, ClientFilterConfig> | undefined,
  skipFields?: ReadonlySet<string>
): DynamicMultiSelectValues {
  return pickDynamicFilterValues(source, filters, skipFields);
}

/** Stable serialized key for effect dependencies when dynamic filter values change. */
export function serializeDynamicFilterValues(
  source: Record<string, unknown> | undefined,
  filters: Record<string, ClientFilterConfig> | undefined,
  skipFields?: ReadonlySet<string>
): string {
  return JSON.stringify(pickDynamicFilterValues(source, filters, skipFields));
}

/** Builds home analytics/query config from saved table config and filter metadata. */
export function buildHomeAnalyticsConfig(
  claimTableConfig: Record<string, unknown>,
  procedureTypeFallback: unknown,
  effectiveDateRangeFilterField: string,
  homePageFilters: Record<string, ClientFilterConfig> | undefined
): HomeAnalyticsConfig {
  return {
    startDate: claimTableConfig.startDate as string | undefined,
    endDate: claimTableConfig.endDate as string | undefined,
    procedureType: normalizeProcedureType(
      claimTableConfig.procedureType ?? procedureTypeFallback
    ),
    search: claimTableConfig.search as string | undefined,
    systemFilter: asStringArray(claimTableConfig.systemFilter),
    dateRangeFilterField: effectiveDateRangeFilterField,
    ...pickDynamicFilterValues(
      claimTableConfig,
      homePageFilters,
      RESERVED_QUEUE_SCALAR_FIELDS
    ),
  };
}

/** Builds initial queue page filter state from saved config and filter metadata. */
export function buildQueuePageFilterState(
  queueTableConfig: Record<string, unknown>,
  effectiveDateRangeFilterField: string,
  queueFilters: Record<string, ClientFilterConfig> | undefined
): QueuePageFilterState {
  return {
    search: (queueTableConfig?.search as string) ?? "",
    startDate: queueTableConfig?.startDate as string | undefined,
    endDate: queueTableConfig?.endDate as string | undefined,
    dateRangeFilterField: effectiveDateRangeFilterField,
    procedureType: normalizeProcedureType(queueTableConfig?.procedureType),
    specialtyReviewFilter: queueTableConfig?.specialtyReviewFilter as
      | string
      | undefined,
    // Only send Escalated filter to GraphQL when true; false/undefined = All (omit arg).
    // false is still stored in queueTableConfig so UI tabs don't re-apply config default.
    ...(queueTableConfig?.needsHumanRsReview === true
      ? { needsHumanRsReview: true }
      : {}),
    ...pickQueueMultiSelectFilterValues(queueTableConfig, queueFilters),
  };
}
